import { ArrowRight, Layers, Sparkles, Users, TrendingUp, DollarSign, User, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';

const ServiceCard = ({ icon: Icon, title, description, features }) => (
  <div className="service-card bg-white border border-gray-200 rounded-xl p-8 group">
    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
      <Icon className="w-7 h-7 text-primary" />
    </div>
    <h3 className="font-oswald uppercase text-xl font-bold text-gray-900 mt-6">
      {title}
    </h3>
    <p className="font-manrope text-sm text-primary mt-2">
      {description}
    </p>
    <ul className="mt-4 space-y-2">
      <li className="font-manrope text-sm text-gray-500 flex items-start gap-2">
        <span className="text-primary">•</span>
        {features[0]}
      </li>
      <li className="font-manrope text-sm text-gray-500 flex items-start gap-2">
        <span className="text-primary">•</span>
        {features[1]}
      </li>
    </ul>
  </div>
);

const GrowthCard = ({ icon: Icon, title, description, features }) => (
  <div className="service-card bg-white border border-gray-200 rounded-xl p-8 group">
    <div className="flex items-start gap-6">
      <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <div>
        <h3 className="font-oswald uppercase text-xl font-bold text-gray-900">
          {title}
        </h3>
        <p className="font-manrope text-sm text-primary mt-1">
          {description}
        </p>
        <ul className="mt-3 space-y-2">
          <li className="font-manrope text-sm text-gray-500 flex items-start gap-2">
            <span className="text-primary">•</span>
            {features[0]}
          </li>
          <li className="font-manrope text-sm text-gray-500 flex items-start gap-2">
            <span className="text-primary">•</span>
            {features[1]}
          </li>
        </ul>
      </div>
    </div>
  </div>
);

export default function Services() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Hero Section */}
      <section
        data-testid="services-hero"
        className="relative pt-32 pb-24 md:pt-40 md:pb-32 bg-gradient-to-br from-white via-green-50/30 to-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Our Services
            </span>
            <h1
              className="font-oswald uppercase tracking-tight font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] mt-4"
              data-testid="services-headline"
            >
              <span className="text-gray-900">Social media is not a channel.</span>
              <br />
              <span className="text-primary">It's a system.</span>
            </h1>
            <p className="font-manrope text-base md:text-lg text-gray-500 mt-6 max-w-2xl">
              Most agencies sell posts or campaigns. We design and operate platform-led growth systems that compound over time.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section
        data-testid="core-services-section"
        className="py-16 md:py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Core Services
            </span>
            <h2 className="font-oswald uppercase tracking-tight font-bold text-2xl md:text-3xl lg:text-4xl text-gray-900 mt-2">
              Foundation & Content
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ServiceCard 
              icon={Layers}
              title="Platform Growth Architecture"
              description="Platform-native strategy"
              features={["Content systems, not one-offs", "Built by former platform operators"]}
            />
            <ServiceCard 
              icon={Sparkles}
              title="Signal-Driven Content Engine"
              description="Platform-first creative"
              features={["Volume + velocity", "Designed for discovery, not polish"]}
            />
            <ServiceCard 
              icon={Users}
              title="Creator Partnership Systems"
              description="Long-term creator relationships"
              features={["Brand-safe, performance-led", "Built for scale, not noise"]}
            />
          </div>
        </div>
      </section>

      {/* Growth & Monetization */}
      <section
        data-testid="growth-services-section"
        className="py-16 md:py-24 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Secondary Services
            </span>
            <h2 className="font-oswald uppercase tracking-tight font-bold text-2xl md:text-3xl lg:text-4xl text-gray-900 mt-2">
              Growth & Monetization
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <GrowthCard 
              icon={TrendingUp}
              title="Paid Signal Amplification"
              description="Organic learnings → paid scale"
              features={["Creative testing loops", "Sustainable ROAS"]}
            />
            <GrowthCard 
              icon={DollarSign}
              title="Creator Business Infrastructure"
              description="Monetization beyond brand deals"
              features={["Commerce, LIVE, affiliates", "IP & long-term income planning"]}
            />
          </div>
        </div>
      </section>

      {/* Advanced / Enterprise */}
      <section
        data-testid="advanced-services-section"
        className="py-16 md:py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
              Advanced Services
            </span>
            <h2 className="font-oswald uppercase tracking-tight font-bold text-2xl md:text-3xl lg:text-4xl text-gray-900 mt-2">
              Enterprise & Visibility
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <GrowthCard 
              icon={User}
              title="Founder Visibility Systems"
              description="Authority building on social platforms"
              features={["Trust-led personal brands", "B2B & high-value positioning"]}
            />
            <GrowthCard 
              icon={Video}
              title="LIVE Commerce Activation"
              description="Pilot-based LIVE strategy"
              features={["Host, product, format testing", "Region-specific execution"]}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        data-testid="services-cta"
        className="py-24 md:py-32 bg-gradient-to-br from-primary/5 via-white to-primary/5 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900">
            Ready to build your
            <span className="text-primary"> growth system?</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-gray-500 mt-4 max-w-xl mx-auto">
            Let's discuss how we can turn your platforms into a long-term business engine for your brand.
          </p>
          <div className="mt-8">
            <a
              href="https://calendly.com/atabdullah-mah/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-cta-button"
            >
              <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-10 py-7 text-lg font-bold uppercase tracking-wide btn-scale glow-primary inline-flex items-center gap-3">
                Book a Call
                <ArrowRight size={20} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
